import React, { useEffect, useState, useContext, useRef } from 'react';
import { Select, DatePicker, Radio, Button, Modal, Table, Checkbox } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import http from '../../../Http';
import utils from '../../../utils';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { Create } from '@material-ui/icons';

const opt = {
    tooltip: {
        trigger: 'axis'
    },
    legend: {
        data: ['PV', 'Battery', 'Grid', 'Consumption', 'SOC']
    },
    xAxis: {
        type: 'category',
        data: [],
        name: '(Time)',
        nameLocation: "center",
        nameGap: 25,
        axisLabel: {
            formatter: (value) => {
                return moment(value).format('HH:mm')
            }
        }
    },
    yAxis: [{
        type: 'value',
        name: 'Power(kW)',
        splitNumber: 5,
    }, {
        type: 'value',
        name: 'SOC(%)',
        alignTicks: true,
        max: 100,
        min: 0,
        splitNumber: 5,
        interval: 20,
    }],
    series: []
}

function Power({ SelectedCombinations, onRefresh }) {
    const { t } = useTranslation();
    const setTip = useContext(utils.tipContext)

    const [data, setData] = useState([]);
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [sns, setSns] = useState([]);

    const refChart = useRef(null);
    const refSNs = useRef([]);

    useEffect(() => {
        opt.legend.data = [t('PV'), t('Battery'), t('Grid'), t('Consumption'), t('SOC')]
        opt.yAxis[0].name = t('Power(kW)')
        opt.yAxis[1].name = t('SOC(%)')
        opt.xAxis.name = t('(Time)')
        opt.yAxis.name = t('(kWh)')

    }, [t])

    useEffect(() => {
        const handleResize = () => {
          if (refChart.current) {
            refChart.current.getEchartsInstance().resize();
          }
        };
    
        window.addEventListener('resize', handleResize);
    
        return () => {
          window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (!SelectedCombinations || SelectedCombinations.length === 0) {
            setSns([]);
            return;
        }
        let SNs = [];
        SelectedCombinations.forEach(v => {
            SNs.push(v.SN);
        })
        if (JSON.stringify(SNs) === JSON.stringify(refSNs.current)) {
            let paralles = SelectedCombinations.length;
            if ( paralles> 0) {
                let tz = SelectedCombinations[0].Timezone??"UTC";
                opt.xAxis.name = t('(Time)') + " - " +tz;

                let ms = {};
                let ls = [];
                SelectedCombinations.forEach((v) => {
                    let dt = utils.convertTime(v["CreateTime"], v["LatestTimezone"], tz);
                    if (dt.substring(0, 10) != date.substring(0, 10)) {
                        return;
                    }
                    ms[v.SN] = {
                        SN: v.SN,
                        CreateTime: paralles==1 ? dt : dt.substring(0, 16)+":00",
                        PPV1: v.PPV1,
                        PPV2: v.PPV2,
                        PBattery: v.PBattery,
                        PAC1Grid: v.PAC1Grid,
                        PAC1Load: v.PAC1Load,
                        BatteryCapacity: v.BatteryCapacity
                    };
                    ls.push(ms[v.SN])
                });


                let replaced = false;
                data.forEach(v=>{
                    if (v.SN) {
                        let r = ms[v.SN];
                        if (r && r["CreateTime"] == v["CreateTime"]) {
                            Object.assign(v, r);
                            replaced = true;
                            let idx = ls.indexOf(r);
                            if (idx>-1) {
                                ls.splice(idx, 1);
                            }
                            delete(ms[v.SN]);
                        }
                    }
                });
                if (!replaced && ls.length > 0) {
                    setData([...data, ...ls]);
                }
            }

            return;
        }
        setSns(SNs);
        refSNs.current = SNs;
    }, [SelectedCombinations])

    useEffect(() => {
        if (!sns || sns.length === 0) {
            return;
        }
        if (loading) {
            return;
        }
        setLoading(true);

        let active = true;

        (async () => {
            let resp = await http.post("integrated-inverter/uhome-singlephase/report/daily/report", {
                SerialNumber: sns.length === 1 ? sns[0] : "",
                SerialNumbers: sns.join(","),
                Date: date,
            }).then(async (r) => {
                return await r.json();
            }).catch((e) => {
                return null;
            });

            if (!active) {
                return;
            }

            if (resp === null || resp.code !== 200) {
                setTip({ open: true, severity: "error", msg: (resp && resp.msg) ? resp.msg : t("load inverter record error") });
                return;
            } else {
                setData(resp.data);

            }

            setLoading(false);
        })();

        return () => {
            active = false;
        };
    }, [sns, date, refresh]);

    useEffect(() => {
        if (!data ) {
            return;
        }
        let paralles = sns?.length??1;
        let rows = {};
        data.forEach(v => {
            let PPV = (parseInt(v.PPV1) + parseInt(v.PPV2));
            let PBattery = v.PBattery > 0x7fffffff ? v.PBattery - 0x100000000 : v.PBattery;
            let PGrid = v.PAC1Grid > 0x7fffffff ? v.PAC1Grid - 0x100000000 : v.PAC1Grid;
            let PLoad = v.PAC1Load;
            let BatteryCapacity = v.BatteryCapacity;

            if (paralles == 1) {
                rows[v.CreateTime] = {
                    CreateTime: v.CreateTime,
                    PPV: PPV,
                    PBattery: PBattery,
                    PGrid: PGrid,
                    PLoad: PLoad,
                    BatteryCapacity: BatteryCapacity,
                }
            } else {
                let row = rows[v.CreateTime];
                if (row) {
                    row.PPV += PPV;
                    row.PBattery += PBattery;
                    row.PGrid += PGrid;
                    row.PLoad += PLoad;
                    row.BatteryCapacity += BatteryCapacity;
                } else {
                    row = {
                        CreateTime: v.CreateTime,
                        PPV: PPV,
                        PBattery: PBattery,
                        PGrid: PGrid,
                        PLoad: PLoad,
                        BatteryCapacity: BatteryCapacity,
                    }
                    rows[v.CreateTime] = row;
                }
            }
        })
        let rs = [];
        let ks = Object.keys(rows)
        ks.sort((a, b) => {
            return moment(a) - moment(b);
        })
        ks.forEach(k => {
            let row = rows[k];
            row.PPV = (row.PPV * 0.001).toFixed(3);
            row.PBattery = (row.PBattery * 0.001).toFixed(3);
            row.PGrid = (row.PGrid * 0.001).toFixed(3);
            row.PLoad = (row.PLoad * 0.001).toFixed(3);
            row.BatteryCapacity = Math.round(row.BatteryCapacity / paralles).toFixed(0);
            rs.push(row);
        })

        const xData = []
        const PPV = []
        const PBattery = []
        const PGrid = []
        const PLoad = []
        const SOC = []
        //遍历结果，给坐标和数据赋值
        rs.forEach(v => {
            xData.push(moment(v.CreateTime).format('YYYY-MM-DD HH:mm'))
            PPV.push(v.PPV)
            PBattery.push(v.PBattery)
            PGrid.push(v.PGrid)
            PLoad.push(v.PLoad)
            SOC.push(v.BatteryCapacity)
        })
        //x轴坐标
        opt.xAxis.data = xData;
        //处理双y轴坐标刻度的对齐
        const max_y0 = Math.max(...PPV, ...PBattery, ...PGrid, ...PLoad)
        const min_y0 = Math.min(...PPV, ...PBattery, ...PGrid, ...PLoad)
        opt.yAxis[0].max = max_y0
        opt.yAxis[0].min = min_y0
        opt.yAxis[0].interval = ((max_y0 - min_y0)/5).toFixed(2)
        //数据
        opt.series = [
            { name: t('PV'), type: 'line', smooth: true, symbol: 'none', data: PPV, yAxisIndex: 0 },
            { name: t('Battery'), type: 'line', smooth: true, symbol: 'none', data: PBattery, yAxisIndex: 0 },
            { name: t('Grid'), type: 'line', smooth: true, symbol: 'none', data: PGrid, yAxisIndex: 0 },
            { name: t('Consumption'), type: 'line', smooth: true, symbol: 'none', data: PLoad, yAxisIndex: 0 },
            { name: t('SOC'), type: 'line', smooth: true, symbol: 'none', data: SOC, yAxisIndex: 1 },
        ]
        //tooltip
        opt.tooltip.formatter = (item) => {
            if (!item || item.length == 0) {
                return ''
            }
            let tip = `<b>${t('Time')}: </b>${item[0].name}<br/>`
            for (const v of item) {
                if (v.seriesName === t('PV')) {
                    tip += `<b>${t('PV')}: </b>${v.value} kW<br/>`
                }
                if (v.seriesName === t('Battery')) {
                    const batPower = v.value
                    let batTip =  batPower < 0 ? t('Battery Discharge') :
                        batPower > 0 ? t('Battery Charge') : t('Battery')
                    
                    tip += `<b>${batTip}: </b>${Math.abs(batPower)} kW<br/>`
                }
                if (v.seriesName === t('Grid')) {
                    const gridPower = v.value
                    let gridTip = gridPower < 0 ? t('Grid Import') :
                        gridPower > 0 ? t('Grid Export') : t('Grid')
                    
                    tip += `<b>${gridTip}: </b>${Math.abs(gridPower)} kW<br/>`
                }
                if (v.seriesName === t('Consumption')) {
                    tip += `<b>${t('Consumption')}: </b>${v.value} kW<br/>`
                }
                if (v.seriesName === t('SOC')) {
                    tip += `<b>${t('SOC')}: </b>${v.value} %<br/>`
                }
            }
            return tip;
        }

        const instance = refChart.current.getEchartsInstance();
        if (instance) {
            instance.setOption(opt, true);
        }
    }, [data]);

    const now = moment().format('YYYY-MM-DD');
    
    return (<div style={{width: '100%', height: '100%',
        display: "flex",
        flexDirection: "row",
    }}>
        <div style={{
            flex:"1 1 auto", 
            padding:"10px", 
            backgroundColor:"#fff",
            display: "flex",
            flexDirection: "column",
            alignItems:"stretch",
        }}>
            <div style={{
                flex:"0 0 auto",
                display:"flex", 
                flexDirection:"row",
                justifyContent:"space-between",
                alignItems:"center",
            }}>
                <div style={{fontSize:"18px", fontWeight:"500", marginBottom:"10px"}}>{t('Input&Output Power')}</div>
                <div style={{flex:"0 0 auto", display:"flex", flexDirection:"row"}}>
                    <DatePicker
                        defaultValue={moment()}
                        value={moment(date)}
                        onChange={(dt, ds) => setDate(dt.format('YYYY-MM-DD'))}
                        allowClear={false}
                    /> 
                    <div style={{width:"10px"}}></div>
                    <Button icon={<ReloadOutlined />} disabled={now != date} style={{  }} onClick={() => {
                        onRefresh(); 
                        setRefresh(!refresh);
                    }} />
                </div>
            </div>
            <div style={{flex:"1 1 auto"}}>
                <ReactECharts ref={refChart} option={opt} style={{width: '100%', height: '100%' }} />
            </div>
        </div>
        <div style={{flex:"0 0 10px"}}></div>
        <div style={{flex:"0 0 330px", backgroundColor:"#fff"}}>
            <div className="notice">
                <div className="title">{t('Notice')}</div>
                <div className="content">{t('None')}</div>
            </div>
            <div className="revenue">
                <div className="title">{t('Other Revenue')}</div>
                <div className="box CO2">
                    <img height="72" src="images/inverter/co2.png" alt="CO2"></img>
                    <div className="num">
                        <span>{0} {t('kG')}</span><br />
                        <span>{t('CO2 Reduction')}</span>
                    </div>
                </div>
                <div className="box coal">
                    <img height="72" src="images/inverter/coal.png" alt={t('Coal')}></img>
                    <div className="num">
                        <span>{0} {t('kG')}</span><br />
                        <span>{t('Coal Saving')}</span>
                    </div>
                </div>
            </div>
        </div>
    </div> );
}

export default Power;